import { message } from "antd";
import type { ArgsProps, JointContent, MessageType } from "antd/es/message/interface";

import { hasRecentGlobalRequestError, shouldSuppressLocalRequestError } from "./requestErrorState";

/**
 * 读取请求错误中可直接展示的后端文案；普通 Error 使用页面兼容的兜底文案。
 */
export function getRequestErrorMessage(error: unknown, fallback: string): string {
    if (!error || typeof error !== "object") return fallback;

    const requestError = error as {
        code?: unknown;
        status?: unknown;
        message?: unknown;
        success?: unknown;
        errors?: unknown;
    };
    const isRequestError = "code" in requestError || "status" in requestError || "success" in requestError;
    // Java 接口常把“参数错误”放在顶层，把真正可读原因放在 errors[].message；优先展示明细。
    const detailMessage = Array.isArray(requestError.errors)
        ? requestError.errors
              .map(item => (item && typeof item === "object" ? (item as { message?: unknown }).message : undefined))
              .find((item): item is string => typeof item === "string" && item.trim().length > 0)
        : undefined;
    const errorMessage = typeof requestError.message === "string" ? requestError.message.trim() : "";

    if (!isRequestError) return fallback;
    return detailMessage?.trim() || errorMessage || fallback;
}

/**
 * 统一展示请求失败提示。请求拦截器已展示后端错误时直接复用该提示，避免同屏出现两条错误消息。
 */
export function showRequestError(error: unknown, fallback: string): boolean {
    const content = getRequestErrorMessage(error, fallback);
    if (hasRecentGlobalRequestError(1500, content)) return false;
    message.error(content);
    return true;
}

let staticMessageDeduperInstalled = false;

/** 从 Antd 静态消息参数中读取可判断的文本内容。 */
const getStaticMessageText = (content: JointContent) => {
    if (typeof content === "string") return content;
    if (typeof content === "number") return String(content);
    if (content && typeof content === "object" && "content" in content) {
        const args = content as ArgsProps;
        return typeof args.content === "string" ? args.content : "";
    }
    return "";
};

/** 返回静态错误消息被统一拦截时可安全忽略的 MessageType。 */
const createNoopMessage = (): MessageType => {
    const noop = (() => undefined) as MessageType;
    noop.then = (onFulfilled, onRejected) => Promise.resolve(true).then(onFulfilled, onRejected);
    return noop;
};

/** 安装全局静态错误 Toast 去重器，兜底覆盖尚未迁移到 showRequestError 的旧页面。 */
export function installRequestErrorMessageDeduper() {
    if (staticMessageDeduperInstalled) return;
    const originalError = message.error.bind(message);
    message.error = ((content: JointContent, duration?: number | VoidFunction, onClose?: VoidFunction) => {
        const text = getStaticMessageText(content);
        const isRequestFailure = /失败|错误|异常|请求|超时|网络|加载|保存|删除|更新|发布|撤回|指派|作废|取消|查询/.test(
            text
        );
        if (isRequestFailure && shouldSuppressLocalRequestError()) return createNoopMessage();
        return originalError(content, duration, onClose);
    }) as typeof message.error;
    staticMessageDeduperInstalled = true;
}
